import axios, { AxiosInstance } from "axios";
import { PackageType } from "@revenuecat/purchases-js";
import { getStripeProducts } from "@/api/stripe";
import {
  RevenuecatPurchasesOfferings,
  RevenuecatStripePackage,
  RevenuecatPurchasesCustomerInfo,
  CustomerInfo,
  SubscriptionEntitlements,
} from "@/types/subscription";

export class Purchases {
  config: { apiKey: string; appUserId: string } = {
    apiKey: "",
    appUserId: "",
  };
  axios: AxiosInstance;

  constructor() {}

  configure(apiKey: string, appUserId: string) {
    this.config.apiKey = apiKey;
    this.config.appUserId = appUserId;

    this.axios = axios.create({
      baseURL: "https://api.revenuecat.com/v1",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
        "X-Platform": "stripe",
      },
    });

    return this;
  }

  async getOfferings() {
    const { data } = await this.axios.get<RevenuecatPurchasesOfferings>(
      `/subscribers/${this.config.appUserId}/offerings`,
    );

    const currentOffering = data.offerings.find(
      (offering) => offering.identifier === data.current_offering_id,
    );
    const productIds = currentOffering.packages.map((pkg) => pkg.platform_product_identifier);
    const stripeProducts = await getStripeProducts(productIds);

    const packagesById: { [key: string]: RevenuecatStripePackage } = {};
    const availablePackages: RevenuecatStripePackage[] = [];

    for (let pkg of currentOffering.packages) {
      const stripeProduct = stripeProducts.find(
        (stripeProduct) => stripeProduct.id === pkg.platform_product_identifier,
      );

      const mappedPkg: RevenuecatStripePackage = {
        identifier: pkg.identifier,
        packageType: PackageType.Custom,
        product: {
          identifier: stripeProduct.id,
          title: stripeProduct.name,
          displayName: stripeProduct.name,
          description: stripeProduct.description,
          currentPrice: {
            amount: stripeProduct.price.unit_amount,
            currency: stripeProduct.price.currency,
            formattedPrice: stripeProduct.price.unit_amount_decimal,
          },
          normalPeriodDuration: "",
          presentedOfferingIdentifier: currentOffering.identifier,
          defaultSubscriptionOption: {},
          subscriptionOptions: {},
        },
      };

      packagesById[pkg.identifier] = {
        identifier: pkg.identifier,
        packageType: PackageType.Custom,
        product: mappedPkg.product,
      };

      availablePackages.push(mappedPkg);
    }

    const offerings = {
      all: {},
      current: {
        identifier: currentOffering.identifier,
        serverDescription: "Packages",
        metadata: currentOffering.metadata,
        packagesById: packagesById,
        availablePackages: availablePackages,
        lifetime: null,
        annual: null,
        sixMonth: null,
        threeMonth: null,
        twoMonth: null,
        monthly: null,
        weekly: null,
      },
    };

    return offerings;
  }

  async getCustomerInfo() {
    const { data } = await this.axios.get<RevenuecatPurchasesCustomerInfo>(
      `/subscribers/${this.config.appUserId}`,
    );

    const activeSubscriptions = new Set<string>();
    const allExpirationDatesByProduct: { [key: string]: Date } = {};
    const allPurchaseDatesByProduct: { [key: string]: Date } = {};
    const entitlements: SubscriptionEntitlements = { all: {}, active: {} };
    let latestEntitlementKey: string | null = null;
    let latestExpiryDate: Date | null = null;

    for (let entitlementKey in data.subscriber.entitlements) {
      const entitlement = data.subscriber.entitlements[entitlementKey];

      const subscriptionKey = Object.keys(data.subscriber.subscriptions).find(
        (key) => key === entitlement.product_identifier,
      );
      const unSubscribedDate =
        subscriptionKey && data.subscriber.subscriptions[subscriptionKey].unsubscribe_detected_at
          ? data.subscriber.subscriptions[subscriptionKey].unsubscribe_detected_at
          : null;

      entitlements.all[entitlementKey] = {
        store: subscriptionKey ? data.subscriber.subscriptions[subscriptionKey].store : null,
        identifier: entitlementKey,
        productIdentifier: entitlement.product_identifier,
        expirationDate: entitlement.expires_date,
        latestPurchaseDate: entitlement.purchase_date,
        unsubscribeDetectedAt: unSubscribedDate,
      };

      const expiryDate = new Date(entitlement.expires_date);
      if (!latestExpiryDate || expiryDate > latestExpiryDate) {
        latestExpiryDate = expiryDate;
        latestEntitlementKey = entitlementKey;
      }
    }

    if (latestEntitlementKey) {
      entitlements.active[latestEntitlementKey] = entitlements.all[latestEntitlementKey];
      activeSubscriptions.add(entitlements.active[latestEntitlementKey].productIdentifier);
    }

    const customerInfo: CustomerInfo = {
      activeSubscriptions: Array.from(activeSubscriptions),
      allExpirationDatesByProduct,
      allPurchaseDatesByProduct,
      firstSeenDate: data.subscriber.first_seen,
      requestDate: data.subscriber.last_seen,
      managementURL: data.subscriber.management_url,
      originalAppUserId: data.subscriber.original_app_user_id,
      originalPurchaseDate: data.subscriber.original_purchase_date,
      entitlements,
    };

    return customerInfo;
  }

  purchase() {}

  async createReceipts(sessionId: string) {
    const { data } = await this.axios.post("/receipts", {
      app_user_id: this.config.appUserId,
      fetch_token: sessionId,
    });

    return data;
  }
}

export default new Purchases();
